import type { NotificationProvider } from "@refinedev/core";
import { notify } from "@/lib/notify";

type Gender = "m" | "f" | "n" | "pl";
type Action = "created" | "updated" | "deleted" | "cloned" | "imported" | "exported";

interface ResourceInfo {
  nom: string;
  gender: Gender;
}

/**
 * Словарь ресурсов с названиями и родом для правильного склонения
 */
const RESOURCES: Record<string, ResourceInfo> = {
  // Telegram
  "telegram-channel": { nom: "Telegram-канал", gender: "m" },
  "telegram-channels": { nom: "Telegram-каналы", gender: "pl" },
  "channel-membership": { nom: "Членство в канале", gender: "n" },
  "channel-memberships": { nom: "Членства в каналах", gender: "pl" },
  // Triggers
  trigger: { nom: "Триггер", gender: "m" },
  triggers: { nom: "Триггеры", gender: "pl" },
  // Teachers
  teacher: { nom: "Учитель", gender: "m" },
  teachers: { nom: "Учителя", gender: "pl" },
  // Reminders
  reminder: { nom: "Напоминание", gender: "n" },
  reminders: { nom: "Напоминания", gender: "pl" },
  // Banners
  banner: { nom: "Баннер", gender: "m" },
  banners: { nom: "Баннеры", gender: "pl" },
  // Courses
  course: { nom: "Курс", gender: "m" },
  courses: { nom: "Курсы", gender: "pl" },
  // Notifications
  notification: { nom: "Уведомление", gender: "n" },
  notifications: { nom: "Уведомления", gender: "pl" },
  // Modules
  module: { nom: "Модуль", gender: "m" },
  modules: { nom: "Модули", gender: "pl" },
  "module-item": { nom: "Урок", gender: "m" },
  "module-items": { nom: "Уроки", gender: "pl" },
  // Subscriptions
  "subscription-plan": { nom: "Абонемент", gender: "m" },
  "subscription-plans": { nom: "Абонементы", gender: "pl" },
  subscription: { nom: "Владелец абонемента", gender: "m" },
  subscriptions: { nom: "Владельцы абонементов", gender: "pl" },
  // Languages
  language: { nom: "Язык", gender: "m" },
  languages: { nom: "Языки", gender: "pl" },
  // Users
  user: { nom: "Пользователь", gender: "m" },
  users: { nom: "Пользователи", gender: "pl" },
  "admin-user": { nom: "Администратор", gender: "m" },
  "admin-users": { nom: "Администраторы", gender: "pl" },
  // Roles & Permissions
  role: { nom: "Роль", gender: "f" },
  roles: { nom: "Роли", gender: "pl" },
  permission: { nom: "Право", gender: "n" },
  permissions: { nom: "Права", gender: "pl" },
  // Bot tokens
  "bot-token": { nom: "Токен бота", gender: "m" },
  "bot-tokens": { nom: "Токены ботов", gender: "pl" },
  // Translations
  "ui-translation": { nom: "Перевод", gender: "m" },
  "ui-translations": { nom: "Переводы", gender: "pl" },
  "translation-key": { nom: "Ключ перевода", gender: "m" },
  "translation-keys": { nom: "Ключи переводов", gender: "pl" },
  "bot-message": { nom: "Сообщение бота", gender: "n" },
  "bot-messages": { nom: "Сообщения бота", gender: "pl" },
  // Products & Orders
  product: { nom: "Товар", gender: "m" },
  products: { nom: "Товары", gender: "pl" },
  order: { nom: "Заказ", gender: "m" },
  orders: { nom: "Заказы", gender: "pl" },
  // Events
  event: { nom: "Событие", gender: "n" },
  events: { nom: "События", gender: "pl" },
  // Broadcasts
  broadcast: { nom: "Рассылка", gender: "f" },
  broadcasts: { nom: "Рассылки", gender: "pl" },
  // Files
  file: { nom: "Файл", gender: "m" },
  files: { nom: "Файлы", gender: "pl" },
  // AI
  "ai-chat": { nom: "AI-чат", gender: "m" },
  "ai-chats": { nom: "AI-чаты", gender: "pl" },
  "ai-message": { nom: "AI-сообщение", gender: "n" },
  "ai-messages": { nom: "AI-сообщения", gender: "pl" },
  // Goals & Intentions
  goal: { nom: "Цель", gender: "f" },
  goals: { nom: "Цели", gender: "pl" },
  intention: { nom: "Намерение", gender: "n" },
  intentions: { nom: "Намерения", gender: "pl" },
  // Knowledge Base
  "knowledge-document": { nom: "Документ базы знаний", gender: "m" },
  "knowledge-documents": { nom: "Документы базы знаний", gender: "pl" },
  // Support
  "support-ticket": { nom: "Обращение в поддержку", gender: "n" },
  "support-tickets": { nom: "Обращения в поддержку", gender: "pl" },
  // Payments
  payment: { nom: "Платёж", gender: "m" },
  payments: { nom: "Платежи", gender: "pl" },
  // Integrations
  integration: { nom: "Интеграция", gender: "f" },
  integrations: { nom: "Интеграции", gender: "pl" },
  // Daily Stats
  "daily-stat": { nom: "Дневная статистика", gender: "f" },
  "daily-stats": { nom: "Дневная статистика", gender: "f" },
  // Subscription Benefits
  "subscription-benefit": { nom: "Преимущество подписки", gender: "n" },
  "subscription-benefits": { nom: "Преимущества подписки", gender: "pl" },
  // API Keys
  "api-key": { nom: "API-ключ", gender: "m" },
  "api-keys": { nom: "API-ключи", gender: "pl" },
};

/**
 * Склонение глаголов по роду
 */
const VERB_FORMS: Record<Action, Record<Gender, string>> = {
  created: { m: "создан", f: "создана", n: "создано", pl: "созданы" },
  updated: { m: "обновлён", f: "обновлена", n: "обновлено", pl: "обновлены" },
  deleted: { m: "удалён", f: "удалена", n: "удалено", pl: "удалены" },
  cloned: { m: "скопирован", f: "скопирована", n: "скопировано", pl: "скопированы" },
  imported: { m: "импортирован", f: "импортирована", n: "импортировано", pl: "импортированы" },
  exported: { m: "экспортирован", f: "экспортирована", n: "экспортировано", pl: "экспортированы" },
};

/**
 * Переводы ошибок
 */
const ERROR_TRANSLATIONS: Record<string, string> = {
  "Error": "Ошибка",
  "Validation Error": "Ошибка валидации",
  "Bad Request": "Некорректный запрос",
  "Unauthorized": "Требуется авторизация",
  "Forbidden": "Доступ запрещён",
  "Not Found": "Не найдено",
  "Not found": "Не найдено",
  "Internal Server Error": "Внутренняя ошибка сервера",
  "Network Error": "Ошибка сети",
  "Something went wrong": "Что-то пошло не так",
  "Created data not returned": "Ошибка сохранения данных",
  "Updated data not returned": "Ошибка сохранения данных",
  "Failed to fetch": "Ошибка подключения к серверу",
  "Service Unavailable": "Сервис недоступен",
  "Gateway Timeout": "Превышено время ожидания",
  "Validation error": "Ошибка валидации",
  "Validation failed": "Ошибка валидации",
  "Record not found": "Запись не найдена",
  "record not found": "Запись не найдена",
  "Duplicate entry": "Запись уже существует",
  "Invalid credentials": "Неверный логин или пароль",
  "Token expired": "Сессия истекла",
  "Invalid token": "Неверный токен",
};

/**
 * Переводит success-сообщение Refine на русский
 */
function translateSuccessMessage(message: string): string {
  const msg = message.trim();

  // Паттерн: "Successfully {action} {article?} {resource}"
  const match = msg.match(/^Successfully (\w+)(?: (?:a|an) )?(.+)$/i);

  if (match) {
    const [, actionEn, resourceKey] = match;
    const action = actionEn.toLowerCase() as Action;
    const resource = RESOURCES[resourceKey.toLowerCase()];

    if (resource && VERB_FORMS[action]) {
      const verb = VERB_FORMS[action][resource.gender];
      return `${resource.nom} ${verb}`;
    }
  }

  // Fallback для неизвестных ресурсов
  if (msg.toLowerCase().includes("successfully created")) {return "Успешно создано";}
  if (msg.toLowerCase().includes("successfully updated")) {return "Успешно обновлено";}
  if (msg.toLowerCase().includes("successfully deleted")) {return "Успешно удалено";}
  if (msg.toLowerCase().includes("successful")) {return "Успешно";}

  return msg;
}

/**
 * Переводит error-сообщение на русский
 */
function translateErrorMessage(message: string): string {
  const msg = message.trim();

  // Точное совпадение
  if (ERROR_TRANSLATIONS[msg]) {
    return ERROR_TRANSLATIONS[msg];
  }

  // HTTP ошибки
  if (msg.startsWith("HTTP ")) {
    if (msg.includes("404")) {return "Ресурс не найден";}
    if (msg.includes("403")) {return "Доступ запрещён";}
    if (msg.includes("401")) {return "Требуется авторизация";}
    if (msg.includes("500")) {return "Ошибка сервера";}
    if (msg.includes("502") || msg.includes("503")) {return "Сервис недоступен";}
    return "Ошибка запроса";
  }

  // Частичное совпадение
  for (const [en, ru] of Object.entries(ERROR_TRANSLATIONS)) {
    if (msg.toLowerCase().includes(en.toLowerCase())) {
      return ru;
    }
  }

  return msg;
}

/**
 * Форматирует ошибку валидации для отображения в toast
 * Если есть несколько строк (ошибки по полям), показывает их списком
 */
function formatValidationError(msg: string): { title: string; description?: string } {
  const lines = msg.split("\n").filter(line => line.trim().length > 0);

  if (lines.length <= 1) {
    return { title: translateErrorMessage(msg) };
  }

  // Первая строка — заголовок, остальные — описание
  const [firstLine, ...rest] = lines;
  const title = translateErrorMessage(firstLine);

  // Форматируем ошибки по полям
  const description = rest
    .map(line => `• ${line}`)
    .join("\n");

  return { title, description };
}

/**
 * NotificationProvider для Sonner
 */
export const notificationProvider: NotificationProvider = {
  open: ({ type, message, key, description }) => {
    const msg = typeof message === "string" ? message : String(message);

    switch (type) {
      case "success": {
        const title = translateSuccessMessage(msg);
        notify.success(title, { id: key, duration: 4000 });
        break;
      }

      case "error": {
        const formatted = formatValidationError(msg);
        // Используем description из параметров или из форматированного сообщения
        const finalDescription = description ?? formatted.description;

        if (finalDescription) {
          notify.error(formatted.title, {
            id: key,
            duration: 8000,
            description: finalDescription,
          });
        } else {
          notify.error(formatted.title, { id: key, duration: 6000 });
        }
        break;
      }

      case "progress":
        notify.loading(msg, { id: key });
        break;

      default:
        notify.info(msg, { id: key });
    }
  },

  close: (key) => {
    notify.dismiss(key);
  },
};

export { translateErrorMessage };
